import React, { useState } from "react";
import { ChevronDown, Filter } from "lucide-react";
import { Mixpanel } from "../../../config/Mixpanel";
import Constants from "../../../common/constants";
import SkillModal from "./SkillModal";
import Companies from "./Companies";
import Institution from "./Institution";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RecruiterProfileMenu from "../RecruiterAuth/RecruiterProfileMenu";

const StickyHeader = ({ onFilterClick, setIsProfileMenuOpen, isProfileMenuOpen, onApplyFilters, setIsRecruiterAuthOpen, showStickyFilters }) => {
  const navigate = useNavigate();
  const recruiterAccessToken = useSelector((store) => store?.hallOfFame?.recruiterAccessToken);
  const recruiterData = useSelector((store) => store?.hallOfFame?.recruiterData);

  return (
    <div className="sticky top-0 z-50 w-full">
      <div className="relative z-20 bg-[#0A1929] px-4 py-2 shadow-lg md:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Top Row: Logo and Profile */}
          <div className="flex items-center justify-between">
            {/* Logo Section */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/recruiter")}>
              <div className="flex w-fit items-center gap-2 rounded-md bg-white/10 p-2">
                <img
                  src="https://assets.learntube.ai/files/new%20Linkedin%20data/image%2011.png?updatedAt=1747309992449"
                  alt="LearnTube Logo"
                  width={24}
                  height={24}
                  className="h-6 w-6"
                />
                <p className="text-xs font-medium text-white">LearnTube.ai</p>
              </div>
              <div className="h-[36px] w-[2px] bg-white/20" />
              <div className="flex flex-col items-start justify-center">
                <p className="text-[10px] text-white/50">backed by</p>
                <div className="flex items-center gap-1">
                  <img
                    src="https://assets.learntube.ai/files/post-payment-updates/use2460.svg?updatedAt=1728356838222"
                    alt="Google G"
                    className="h-3 w-auto"
                  />
                  <img
                    src="https://assets.learntube.ai/files/post-payment-updates/text3075.svg?updatedAt=1728356926579"
                    alt="Google for Startups"
                    className="h-3 w-auto"
                  />
                </div>
              </div>
            </div>

            {/* Profile Icon */}
            <div className="relative">
              <div className="flex h-10 w-20 items-center justify-center rounded-full bg-[#1A3855] text-white gap-2 cursor-pointer" onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}>
                <img 
                  src={recruiterData?.profile_image || "https://assets.learntube.ai/files/halloffame/recruiter/new-user-icon.svg"} 
                  alt="User" 
                  className="h-5 w-5 rounded-full"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://assets.learntube.ai/files/halloffame/recruiter/new-user-icon.svg";
                  }}
                />
                <img src="https://assets.learntube.ai/files/halloffame/recruiter/breadcrums.svg" alt="breadcrumbs" className="h-5 w-5" />
              </div>
              <RecruiterProfileMenu isOpen={isProfileMenuOpen} onClose={() => setIsProfileMenuOpen(false)} onLogin={() => setIsRecruiterAuthOpen(true)} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row: Filters (Absolute Positioned) */}
      <div className={`absolute top-[90%] left-0 -z-10 w-full bg-[#0A1929] px-4 pb-2 shadow-lg transition-all duration-300 md:px-8 ${showStickyFilters ? 'translate-y-0 opacity-100 visible' : '-translate-y-full opacity-0 invisible'}`}>
        <div className="mx-auto max-w-7xl pt-2">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            <FilterPill label="Skill" onApplyFilters={onApplyFilters} setIsRecruiterAuthOpen={setIsRecruiterAuthOpen} />
            <FilterPill label="Company" onApplyFilters={onApplyFilters} setIsRecruiterAuthOpen={setIsRecruiterAuthOpen} />
            <FilterPill label="Institution" onApplyFilters={onApplyFilters} setIsRecruiterAuthOpen={setIsRecruiterAuthOpen} />

            <div className="ml-auto pl-2">
              <button
                onClick={() => {
                  Mixpanel.track(Constants.Mixpanel.RECRUITER_RESULTS_PAGE_VIEW_FILTERS_CLICKED);
                  if (!recruiterAccessToken) {
                    setIsRecruiterAuthOpen(true);
                    return;
                  }
                  onFilterClick();
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1A3855] text-white transition-colors hover:bg-[#234668]"
              >
                <Filter size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterPill = ({ label, onApplyFilters, setIsRecruiterAuthOpen }) => {
  const [skillModal, setSkillModal] = useState(false);
  const [companyModal, setCompanyModal] = useState(false);
  const [institutionModal, setInstitutionModal] = useState(false);

  const recruiterAccessToken = useSelector((store) => store?.hallOfFame?.recruiterAccessToken);

  const onFilterClick = () => {
    if (!recruiterAccessToken) {
      setIsRecruiterAuthOpen(true);
      return;
    }
    if (label === "Skill") {
      Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_SKILLS_CLICKED);
      setSkillModal(true);
    } else if (label === "Company") {
      Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_COMPANIES_CLICKED);
      setCompanyModal(true);
    } else if (label === "Institution") {
      Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_INSTITUTIONS_CLICKED);
      setInstitutionModal(true);
    }
  };

  return (
    <>
      <button className="flex min-w-fit items-center gap-2 rounded-full bg-[#1A3855] px-4 py-2 text-sm text-white transition-colors hover:bg-[#234668]" onClick={onFilterClick}>
        <span>{label}</span>
        <ChevronDown size={16} className="text-gray-400" />
      </button>
      <SkillModal isOpen={skillModal} onClose={() => setSkillModal(false)} onApplyFilters={onApplyFilters} />
      <Companies isOpen={companyModal} onClose={() => setCompanyModal(false)} onApplyFilters={onApplyFilters} />
      <Institution isOpen={institutionModal} onClose={() => setInstitutionModal(false)} onApplyFilters={onApplyFilters} />
    </>
  );
};

export default StickyHeader;
